import { useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Typography,
  TextField,
  InputAdornment,
  Snackbar,
  Alert,
  Box,
} from "@mui/material";
import {
  Share as ShareIcon,
  Facebook,
  Twitter,
  WhatsApp,
  Email,
  ContentCopy,
  Check,
  Close,
} from "@mui/icons-material";

export default function PropertyShareButton({
  propertyTitle,

  propertyLocation,
  propertyUrl,
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // Texto personalizado para propiedades inmobiliarias
  const shareText = `🏠 ${propertyTitle}\n💰📍 ${propertyLocation}\n\n¡Mira esta increíble propiedad!`;
  const shareTitle = `Propiedad en ${propertyLocation}`;

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const showSnackbar = (message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(propertyUrl);
      setCopied(true);
      showSnackbar("¡Enlace copiado al portapapeles!");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      showSnackbar("Error al copiar el enlace");
    }
  };

  const shareOnFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      propertyUrl + "&s=f"
    )}`;
    console.log(url);
    window.open(url, "_blank", "width=600,height=400");
  };

  const shareOnTwitter = () => {
    const url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(
      propertyUrl + "&s=x"
    )}&text=${encodeURIComponent(shareText)}`;
    window.open(url, "_blank", "width=600,height=400");
  };

  const shareOnLinkedIn = () => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
      propertyUrl
    )}`;
    window.open(url, "_blank", "width=600,height=400");
  };
  // Función para manejar el compartir en Instagram
  const handleShareToInstagram = () => {
    // Instagram no tiene una API directa para compartir desde web
    // Estas son las opciones posibles:

    if (navigator.share) {
      // Usar la Web Share API si está disponible (solo móvil)
      navigator
        .share({
          title: "title",
          text: "caption",
          url: "url",
        })
        .catch((error) => console.log("Error al compartir:", error));
    } else if (propertyUrl) {
      // Opción 1: Abrir en la app de Instagram con un enlace especial
      // Nota: Esto solo funciona en dispositivos móviles con Instagram instalado
      const instagramUrl = `instagram://library?AssetPath=${encodeURIComponent(
        propertyUrl
      )}&caption=${encodeURIComponent("caption")}`;
      window.location.href = instagramUrl;

      // Si no se abre la app, redirigir a la web de Instagram
      setTimeout(() => {
        window.location.href = "https://www.instagram.com/";
      }, 250);
    } else {
      // Opción 2: Abrir la web de Instagram en una nueva pestaña
      window.open(
        `https://www.instagram.com/?url=${encodeURIComponent(propertyUrl)}`,
        "_blank"
      );
    }
  };
  const shareOnWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(
      shareText + "\n\n" + propertyUrl
    )}`;
    window.open(url, "_blank");
  };

  const shareByEmail = () => {
    const subject = encodeURIComponent(shareTitle);
    const body = encodeURIComponent(
      `${shareText}\n\nVer propiedad: ${propertyUrl}`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <>
      {/* Botón estático */}
      <IconButton
        onClick={handleOpen}
        sx={{
          color: "primary.main",
          fontSize: "0.8rem",
          gap: "3px",
          marginTop: "8px",
          "&:hover": {
            backgroundColor: "primary.main",
            color: "white",
          },
        }}
        aria-label="compartir propiedad"
      >
        Compartir
        <ShareIcon />
      </IconButton>

      {/* Dialog/Modal */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" color={"white"} component="div">
              Compartir Propiedad
            </Typography>
            <IconButton onClick={handleClose} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          {/* Información de la propiedad */}
          <Box
            sx={{ mb: 3, p: 2, backgroundColor: "#f8f9fa", borderRadius: 2 }}
          >
            <Typography
              variant="subtitle1"
              color={"black"}
              fontWeight="bold"
              gutterBottom
            >
              {propertyTitle}
            </Typography>

            <Typography variant="body2" color={"black"}>
              📍 {propertyLocation}
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Comparte esta propiedad en tus redes sociales
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* Facebook */}
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Facebook />}
                onClick={shareOnFacebook}
                sx={{
                  py: 1.5,
                  color: "#1877F2",
                  borderColor: "#1877F2",
                  "&:hover": {
                    backgroundColor: "#1877F2",
                    color: "white",
                  },
                }}
              >
                Facebook
              </Button>
            </Grid>

            {/* Twitter */}
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Twitter />}
                onClick={shareOnTwitter}
                sx={{
                  py: 1.5,
                  color: "#1DA1F2",
                  borderColor: "#1DA1F2",
                  "&:hover": {
                    backgroundColor: "#1DA1F2",
                    color: "white",
                  },
                }}
              >
                Twitter
              </Button>
            </Grid>

            {/* LinkedIn */}
            {/*  <Grid item xs={6}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Instagram />}
                onClick={handleShareToInstagram}
                sx={{
                  py: 1.5,
                  color: "#E1306C",
                  borderColor: "#E1306C",
                  "&:hover": {
                    backgroundColor: "#E1306C",
                    color: "white",
                  },
                }}
              >
                Instagram
              </Button>
            </Grid> */}

            {/* WhatsApp */}
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<WhatsApp />}
                onClick={shareOnWhatsApp}
                sx={{
                  py: 1.5,
                  color: "#25D366",
                  borderColor: "#25D366",
                  "&:hover": {
                    backgroundColor: "#25D366",
                    color: "white",
                  },
                }}
              >
                WhatsApp
              </Button>
            </Grid>

            {/* Email */}
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Email />}
                onClick={shareByEmail}
                sx={{
                  py: 1.5,
                  color: "#EA4335",
                  borderColor: "#EA4335",
                  "&:hover": {
                    backgroundColor: "#EA4335",
                    color: "white",
                  },
                }}
              >
                Email
              </Button>
            </Grid>

            {/* Copiar enlace */}
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={copied ? <Check /> : <ContentCopy />}
                onClick={copyToClipboard}
                sx={{
                  py: 1.5,
                  color: copied ? "#4CAF50" : "#757575",
                  borderColor: copied ? "#4CAF50" : "#757575",
                  "&:hover": {
                    backgroundColor: copied ? "#4CAF50" : "#757575",
                    color: "white",
                  },
                }}
              >
                {copied ? "Copiado" : "Copiar"}
              </Button>
            </Grid>
          </Grid>

          {/* Campo de URL */}
          <TextField
            fullWidth
            value={propertyUrl}
            variant="outlined"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={copyToClipboard} edge="end">
                    {copied ? <Check color="success" /> : <ContentCopy />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              color: "black",
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#f5f5f5",
              },
            }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleClose} variant="contained" fullWidth>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}
